import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { siteRouter } from "../../api/endpoints/siteNetwork";
import { qk } from "../../api/queryKeys";
import { ApiError } from "../../api/errors";
import { vaultFieldRefError } from "../../lib/fieldValidators";
import type { RouterConfig, RouterIn } from "../../api/types/siteNetwork";
import type { NetworkDefaultsOut } from "../../api/types/networkDefaults";
import { FormShell } from "../../components/forms/FormShell";
import { TextField } from "../../components/forms/TextField";
import { CheckboxField } from "../../components/forms/CheckboxField";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { EmptyState } from "../../components/ui/EmptyState";
import { StringListField } from "../../components/forms/StringListField";
import { useNetworkDefaults } from "./useNetworkDefaults";
import { GeneratedFromAllocationBanner } from "./GeneratedFromAllocationBanner";

type CreateMode = "idle" | "manual" | "prefill";

// MikroTik / RouterOS-first defaults for a brand-new router config. The schema
// stays vendor-generic; these are just sensible starting values + the operator
// edits from here. RouterOS-only knobs go in vendor_extras (advanced).
const MIKROTIK_DEFAULTS: RouterIn = {
  router_kind: "routeros",
  identity: "",
  wan_dhcp: true,
  dns_servers: [],
  ntp_servers: [],
  bootstrap_username: "admin",
  bootstrap_secret_ref: null,
  ops_secret_ref: null,
  break_glass_secret_ref: null,
  mgmt_bootstrap_host: null,
  mgmt_managed_host: null,
  mgmt_access_interface: null,
  mgmt_tagged_uplinks: [],
  backup_local_export_dir: null,
  vendor_extras: {},
  notes: [],
};

export function RouterEditor({
  siteSlug,
  canWrite,
}: {
  siteSlug: string;
  canWrite: boolean;
}) {
  const [mode, setMode] = useState<CreateMode>("idle");
  const [prefillAttempt, setPrefillAttempt] = useState(0);
  const startPrefill = () => {
    setPrefillAttempt((n) => n + 1); // fresh fetch even on retry / re-entry
    setMode("prefill");
  };
  const q = useQuery({
    // 404 is the "not configured yet" state, not an error to retry.
    retry: false,
    queryKey: qk.siteRouter(siteSlug),
    queryFn: () => siteRouter.get(siteSlug).then((r) => r.data),
  });
  // Fetched only once the operator clicks "Prefill from allocation".
  const defaults = useNetworkDefaults(siteSlug, {
    enabled: mode === "prefill",
    attempt: prefillAttempt,
  });

  if (q.isPending) {
    return <p style={{ color: "var(--text-dim)" }}>Loading router…</p>;
  }

  const is404 = q.error instanceof ApiError && q.error.status === 404;
  if (q.error && !is404) {
    return <ErrorBanner error={q.error} title="Failed to load router" />;
  }

  if (q.data) {
    return (
      <RouterForm
        key={`existing-${siteSlug}`}
        siteSlug={siteSlug}
        canWrite={canWrite}
        initial={toIn(q.data)}
        existing
      />
    );
  }

  // 404 → not configured.
  if (mode === "manual") {
    return (
      <RouterForm
        key={`new-${siteSlug}`}
        siteSlug={siteSlug}
        canWrite={canWrite}
        initial={MIKROTIK_DEFAULTS}
        onCancel={() => setMode("idle")}
      />
    );
  }
  if (mode === "prefill") {
    if (defaults.isPending) {
      return (
        <p style={{ color: "var(--text-dim)" }}>
          Computing router defaults from this site's allocation…
        </p>
      );
    }
    if (defaults.error) {
      return (
        <div style={{ display: "grid", gap: 12 }}>
          <ErrorBanner error={defaults.error} title="Can't prefill from allocation yet" />
          <PrefillActions canWrite={canWrite} onPrefill={startPrefill} onManual={() => setMode("manual")} />
        </div>
      );
    }
    if (defaults.data) {
      return (
        <RouterForm
          key={`prefill-${siteSlug}-${prefillAttempt}`}
          siteSlug={siteSlug}
          canWrite={canWrite}
          initial={defaults.data.router}
          source={defaults.data.source}
          onCancel={() => setMode("idle")}
        />
      );
    }
  }
  return (
    <EmptyState
      title="No router configured"
      description="This site has no router configuration yet."
      action={
        canWrite ? (
          <PrefillActions canWrite={canWrite} onPrefill={startPrefill} onManual={() => setMode("manual")} />
        ) : null
      }
    />
  );
}

function PrefillActions({
  canWrite,
  onPrefill,
  onManual,
}: {
  canWrite: boolean;
  onPrefill: () => void;
  onManual: () => void;
}) {
  if (!canWrite) return null;
  return (
    <span style={{ display: "inline-flex", gap: 8 }}>
      <button type="button" onClick={onPrefill} style={primaryBtn}>
        Prefill from allocation
      </button>
      <button type="button" onClick={onManual} style={secondaryBtn}>
        Configure router (MikroTik)
      </button>
    </span>
  );
}

// ---------------------------------------------------------------------------

interface RouterDraft {
  router_kind: string;
  identity: string;
  wan_dhcp: boolean;
  dns_servers: string[];
  ntp_servers: string[];
  bootstrap_username: string;
  bootstrap_secret_ref: string;
  ops_secret_ref: string;
  break_glass_secret_ref: string;
  mgmt_bootstrap_host: string;
  mgmt_managed_host: string;
  mgmt_access_interface: string;
  mgmt_tagged_uplinks: string[];
  backup_local_export_dir: string;
  vendor_extras_json: string;
  notes: string[];
}

function RouterForm({
  siteSlug,
  canWrite,
  initial,
  existing,
  onCancel,
  source,
}: {
  siteSlug: string;
  canWrite: boolean;
  initial: RouterIn;
  existing?: boolean;
  onCancel?: () => void;
  source?: NetworkDefaultsOut["source"];
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<RouterDraft>(() => toDraft(initial));
  const set = <K extends keyof RouterDraft>(k: K, v: RouterDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = useMutation({
    mutationFn: (body: RouterIn) => siteRouter.put(siteSlug, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.siteRouter(siteSlug) });
      onCancel?.();
    },
  });
  const del = useMutation({
    mutationFn: () => siteRouter.remove(siteSlug),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.siteRouter(siteSlug) }),
  });

  const vendor = parseVendorExtras(draft.vendor_extras_json);
  const secretErrors = {
    bootstrap_secret_ref: vaultFieldRefError(draft.bootstrap_secret_ref),
    ops_secret_ref: vaultFieldRefError(draft.ops_secret_ref),
    break_glass_secret_ref: vaultFieldRefError(draft.break_glass_secret_ref),
  };
  const hasSecretError = Object.values(secretErrors).some((e) => e !== null);
  const isValid = vendor.ok && !hasSecretError;
  const pending = save.isPending || del.isPending;
  const disabled = !canWrite || pending;

  return (
    <FormShell
      submitLabel={existing ? "Save router" : "Create router"}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isValid || disabled) return;
        save.mutate(fromDraft(draft, vendor.ok ? vendor.value : {}));
      }}
      onCancel={onCancel}
      pending={pending}
      submitDisabled={!canWrite}
      error={save.error ?? del.error}
      errorTitle="Router save failed"
      footer={
        existing && canWrite ? (
          <div>
            <button
              type="button"
              style={dangerBtn}
              disabled={pending}
              onClick={() => {
                if (window.confirm(`Delete router config for ${siteSlug}?`)) del.mutate();
              }}
            >
              Delete router config
            </button>
          </div>
        ) : undefined
      }
    >
      {source && <GeneratedFromAllocationBanner source={source} />}
      <TextField
        label="Router kind"
        value={draft.router_kind}
        onChange={(v) => set("router_kind", v)}
        placeholder="routeros"
        helper="Vendor/OS, e.g. routeros (MikroTik)."
        disabled={disabled}
      />
      <TextField
        label="Identity (hostname)"
        value={draft.identity}
        onChange={(v) => set("identity", v)}
        placeholder="abdullah-raya-router-01"
        disabled={disabled}
      />
      <CheckboxField
        label="WAN via DHCP"
        checked={draft.wan_dhcp}
        onChange={(v) => set("wan_dhcp", v)}
        helper="MikroTik WAN on ether1. Uncheck for a static WAN."
        disabled={disabled}
      />
      <StringListField
        label="DNS servers"
        value={draft.dns_servers}
        onChange={(v) => set("dns_servers", v)}
        placeholder={"1.1.1.1\n1.0.0.1"}
        helper="One per line."
        disabled={disabled}
      />
      <StringListField
        label="NTP servers"
        value={draft.ntp_servers}
        onChange={(v) => set("ntp_servers", v)}
        placeholder={"time.cloudflare.com\npool.ntp.org"}
        helper="One per line."
        disabled={disabled}
      />

      <TextField
        label="Mgmt bootstrap host"
        value={draft.mgmt_bootstrap_host}
        onChange={(v) => set("mgmt_bootstrap_host", v)}
        placeholder="10.128.0.1"
        helper="Initial reachability address (mgmt VLAN gateway)."
        disabled={disabled}
      />
      <TextField
        label="Mgmt managed host"
        value={draft.mgmt_managed_host}
        onChange={(v) => set("mgmt_managed_host", v)}
        placeholder="10.128.0.1"
        helper="Ops reachability address."
        disabled={disabled}
      />
      <TextField
        label="Mgmt access interface (break-glass)"
        value={draft.mgmt_access_interface}
        onChange={(v) => set("mgmt_access_interface", v)}
        placeholder="ether5"
        helper="Physical break-glass port."
        disabled={disabled}
      />
      <StringListField
        label="Tagged uplinks"
        value={draft.mgmt_tagged_uplinks}
        onChange={(v) => set("mgmt_tagged_uplinks", v)}
        placeholder="ether2"
        helper="Trunk ports to the switch. One per line."
        disabled={disabled}
      />
      <TextField
        label="Backup local export dir"
        value={draft.backup_local_export_dir}
        onChange={(v) => set("backup_local_export_dir", v)}
        disabled={disabled}
      />

      {/* Secrets: Vault references only — never paste plaintext. */}
      <TextField
        label="Bootstrap username"
        value={draft.bootstrap_username}
        onChange={(v) => set("bootstrap_username", v)}
        placeholder="admin"
        disabled={disabled}
      />
      <TextField
        label="Bootstrap secret ref"
        value={draft.bootstrap_secret_ref}
        onChange={(v) => set("bootstrap_secret_ref", v)}
        placeholder="vault:tenants/<slug>/router/bootstrap_admin_password#password"
        helper="A vault: reference — never a plaintext secret."
        error={secretErrors.bootstrap_secret_ref}
        disabled={disabled}
      />
      <TextField
        label="Ops secret ref"
        value={draft.ops_secret_ref}
        onChange={(v) => set("ops_secret_ref", v)}
        placeholder="vault:tenants/<slug>/router/ops_admin_password#password"
        error={secretErrors.ops_secret_ref}
        disabled={disabled}
      />
      <TextField
        label="Break-glass secret ref"
        value={draft.break_glass_secret_ref}
        onChange={(v) => set("break_glass_secret_ref", v)}
        placeholder="vault:tenants/<slug>/router/break_glass#password"
        error={secretErrors.break_glass_secret_ref}
        disabled={disabled}
      />

      <StringListField
        label="Notes"
        value={draft.notes}
        onChange={(v) => set("notes", v)}
        helper="One per line."
        disabled={disabled}
      />

      {/* Advanced: RouterOS-specific knobs the generic schema doesn't model. */}
      <details>
        <summary style={{ cursor: "pointer", fontSize: "0.8rem", color: "var(--text-dim)" }}>
          Vendor extras (advanced, JSON)
        </summary>
        <textarea
          value={draft.vendor_extras_json}
          disabled={disabled}
          rows={6}
          onChange={(e) => set("vendor_extras_json", e.target.value)}
          placeholder={'{\n  "wan_interface": "ether1",\n  "lan_bridge_name": "bridge"\n}'}
          style={{
            width: "100%",
            marginTop: 8,
            padding: "8px 10px",
            background: "var(--surface-2)",
            border: `1px solid ${vendor.ok ? "var(--border)" : "var(--crit)"}`,
            borderRadius: 4,
            color: "var(--text)",
            fontFamily: "monospace",
            fontSize: "0.85rem",
            resize: "vertical",
          }}
        />
        {!vendor.ok && (
          <span style={{ fontSize: "0.75rem", color: "var(--crit)" }}>{vendor.error}</span>
        )}
      </details>
    </FormShell>
  );
}

// ---- helpers ----

type VendorParse =
  | { ok: true; value: Record<string, unknown> }
  | { ok: false; error: string };

function parseVendorExtras(text: string): VendorParse {
  const t = text.trim();
  if (t === "") return { ok: true, value: {} };
  let parsed: unknown;
  try {
    parsed = JSON.parse(t);
  } catch {
    return { ok: false, error: "Not valid JSON." };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ok: false, error: "Must be a JSON object." };
  }
  return { ok: true, value: parsed as Record<string, unknown> };
}

function toIn(c: RouterConfig): RouterIn {
  // RouterConfig extends RouterIn with site_id; strip it for the form/PUT body.
  const { site_id: _site_id, ...rest } = c;
  return rest;
}

function toDraft(r: RouterIn): RouterDraft {
  const hasExtras = Object.keys(r.vendor_extras ?? {}).length > 0;
  return {
    router_kind: r.router_kind ?? "",
    identity: r.identity ?? "",
    wan_dhcp: r.wan_dhcp,
    dns_servers: r.dns_servers,
    ntp_servers: r.ntp_servers,
    bootstrap_username: r.bootstrap_username ?? "",
    bootstrap_secret_ref: r.bootstrap_secret_ref ?? "",
    ops_secret_ref: r.ops_secret_ref ?? "",
    break_glass_secret_ref: r.break_glass_secret_ref ?? "",
    mgmt_bootstrap_host: r.mgmt_bootstrap_host ?? "",
    mgmt_managed_host: r.mgmt_managed_host ?? "",
    mgmt_access_interface: r.mgmt_access_interface ?? "",
    mgmt_tagged_uplinks: r.mgmt_tagged_uplinks,
    backup_local_export_dir: r.backup_local_export_dir ?? "",
    vendor_extras_json: hasExtras ? JSON.stringify(r.vendor_extras, null, 2) : "",
    notes: r.notes,
  };
}

function fromDraft(d: RouterDraft, vendorExtras: Record<string, unknown>): RouterIn {
  const orNull = (s: string) => (s.trim() === "" ? null : s.trim());
  return {
    router_kind: orNull(d.router_kind),
    identity: orNull(d.identity),
    wan_dhcp: d.wan_dhcp,
    dns_servers: d.dns_servers,
    ntp_servers: d.ntp_servers,
    bootstrap_username: orNull(d.bootstrap_username),
    bootstrap_secret_ref: orNull(d.bootstrap_secret_ref),
    ops_secret_ref: orNull(d.ops_secret_ref),
    break_glass_secret_ref: orNull(d.break_glass_secret_ref),
    mgmt_bootstrap_host: orNull(d.mgmt_bootstrap_host),
    mgmt_managed_host: orNull(d.mgmt_managed_host),
    mgmt_access_interface: orNull(d.mgmt_access_interface),
    mgmt_tagged_uplinks: d.mgmt_tagged_uplinks,
    backup_local_export_dir: orNull(d.backup_local_export_dir),
    vendor_extras: vendorExtras,
    notes: d.notes,
  };
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--text-dim)",
  border: "1px solid var(--border)",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  cursor: "pointer",
};

const dangerBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--crit)",
  border: "1px solid var(--crit)",
  padding: "6px 12px",
  borderRadius: 4,
  fontSize: "0.85rem",
  cursor: "pointer",
};
